import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useConsent } from "../contexts/ConsentContext";
import { useLanguage } from "../contexts/LanguageContext";
import { consentCopy } from "../data/privacy";

function ConsentSwitch({ checked, label, onChange, status }) {
  return (
    <div className="cookie-preferences-control">
      <span className="cookie-preferences-status">{status}</span>
      <button
        type="button"
        className="cookie-preferences-switch"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
      >
        <span aria-hidden="true" />
      </button>
    </div>
  );
}

export default function CookiePreferencesDialog() {
  const { language } = useLanguage();
  const {
    categories,
    preferencesOpen,
    closePreferences,
    acceptAll,
    rejectAll,
    savePreferences,
  } = useConsent();
  const copy = consentCopy[language] || consentCopy.it;
  const [draft, setDraft] = useState(categories);

  useEffect(() => {
    if (preferencesOpen) setDraft(categories);
  }, [categories, preferencesOpen]);

  return (
    <Dialog.Root
      open={preferencesOpen}
      onOpenChange={(open) => {
        if (!open) closePreferences();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="cookie-preferences-overlay" data-consent-ui />
        <Dialog.Content
          className="cookie-preferences-dialog"
          aria-describedby="cookie-preferences-description"
          data-consent-ui
        >
          <div className="cookie-preferences-head">
            <div>
              <Dialog.Title>{copy.preferencesTitle}</Dialog.Title>
              <Dialog.Description id="cookie-preferences-description">
                {copy.preferencesIntro}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="cookie-consent-close"
                aria-label={copy.closePreferences}
                title={copy.closePreferences}
              >
                <span aria-hidden="true">×</span>
              </button>
            </Dialog.Close>
          </div>

          <section className="cookie-preferences-category">
            <div>
              <h3>{copy.necessaryTitle}</h3>
              <p>{copy.necessaryDescription}</p>
            </div>
            <span className="cookie-preferences-always">{copy.alwaysActive}</span>
          </section>

          <section className="cookie-preferences-category">
            <div>
              <h3>{copy.audienceTitle}</h3>
              <p>{copy.audienceDescription}</p>
            </div>
            <ConsentSwitch
              checked={draft.audienceMeasurement}
              label={copy.audienceLabel}
              status={draft.audienceMeasurement ? copy.enabled : copy.disabled}
              onChange={(audienceMeasurement) =>
                setDraft((current) => ({ ...current, audienceMeasurement }))
              }
            />
          </section>

          <section className="cookie-preferences-category">
            <div>
              <h3>{copy.experienceTitle}</h3>
              <p>{copy.experienceDescription}</p>
            </div>
            <ConsentSwitch
              checked={draft.experienceAnalytics}
              label={copy.experienceLabel}
              status={draft.experienceAnalytics ? copy.enabled : copy.disabled}
              onChange={(experienceAnalytics) =>
                setDraft((current) => ({ ...current, experienceAnalytics }))
              }
            />
          </section>

          <div className="cookie-preferences-actions">
            <button type="button" onClick={() => rejectAll(language)}>
              {copy.rejectAll}
            </button>
            <button type="button" onClick={() => savePreferences(draft, language)}>
              {copy.save}
            </button>
            <button type="button" onClick={() => acceptAll(language)}>
              {copy.accept}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
